/**
 * Chess Board Base Component
 * Pure logic - 8x8 grid rendering and piece finding, no styling
 */

import type { Piece } from "@cloudnest/augmented-chess-engine";
import type { BoardProps } from "../../types";
import { FILES, RANKS } from "../../utils";

export interface BoardBaseProps extends BoardProps {
  /** Function to render each square (allows different styling variants) */
  renderSquare: (props: {
    file: BoardProps["gameState"]["pieces"][0]["position"]["file"];
    rank: BoardProps["gameState"]["pieces"][0]["position"]["rank"];
    piece: Piece | null;
    isSelected: boolean;
    isLegalMove: boolean;
    isCapture: boolean;
    isAugmented: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  /** CSS class name for the board container */
  className?: string;
  /** Inline styles for the board container */
  style?: React.CSSProperties;
}

export function BoardBase({
  gameState,
  selectedPieceId,
  legalMoves,
  onSquareClick,
  isMyTurn: _isMyTurn,
  gameEnded: _gameEnded,
  pieceAugmentations,
  renderSquare,
  className,
  style,
}: BoardBaseProps) {
  return (
    <div className={className} style={style} data-testid="chess-board">
      {RANKS.map((rank) =>
        FILES.map((file) => {
          const piece = gameState.pieces.find(
            (p) => p.position.file === file && p.position.rank === rank,
          );
          const isSelected = piece?.id === selectedPieceId;
          const isLegalMove = legalMoves.some(
            (m) => m.to.file === file && m.to.rank === rank,
          );
          const isCapture = legalMoves.some(
            (m) => m.to.file === file && m.to.rank === rank && m.isCapture,
          );
          const augCategories = piece
            ? pieceAugmentations?.[piece.id]
            : undefined;
          const isAugmented =
            augCategories !== undefined && augCategories.length > 0;

          return renderSquare({
            file,
            rank,
            piece: piece ?? null,
            isSelected,
            isLegalMove,
            isCapture,
            isAugmented,
            onClick: () => onSquareClick(file, rank),
          });
        }),
      )}
    </div>
  );
}
